// Add this at the very top of the file to declare the module for TypeScript
// @ts-ignore
// eslint-disable-next-line
// @ts-nocheck
import React, { useRef, useState, useEffect } from 'react';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import ePub from 'epubjs';

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (book: {
    file: File | null;
    title: string;
    author: string;
    genre: string;
    cover: File | null;
    setUploadProgress: React.Dispatch<React.SetStateAction<number | null>>;
  }) => void;
}

const genres = [
  'Adventure', 'Fantasy', 'Science Fiction', 'Mystery', 'Romance', 'Non-fiction', 'Biography', 'History', 'Children', 'Other'
];

// Improved title extraction utility
function formatTitleFromFilename(filename: string): string {
  const nameWithoutExt = filename.replace(/\.[^/.]+$/, '');
  return nameWithoutExt
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Utility to convert a data URL to a File object
function dataURLtoFile(dataurl: string, filename: string): File {
  const arr = dataurl.split(','), mime = arr[0].match(/:(.*?);/)[1], bstr = atob(arr[1]), n = bstr.length, u8arr = new Uint8Array(n);
  for (let i = 0; i < n; i++) u8arr[i] = bstr.charCodeAt(i);
  return new File([u8arr], filename, { type: mime });
}

export const UploadModal: React.FC<UploadModalProps> = ({ isOpen, onClose, onUpload }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [cover, setCover] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [genre, setGenre] = useState('Adventure');
  const [thumbnail, setThumbnail] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  const allowedTypes = [
    'application/pdf',
    'application/epub+zip',
    'application/x-mobipocket-ebook',
    'text/plain',
    '.pdf', '.epub', '.mobi', '.txt'
  ];

  useEffect(() => {
    if (isOpen) {
      setFile(null);
      setCover(null);
      setTitle('');
      setAuthor('');
      setGenre('Adventure');
      setThumbnail(null);
      setDragActive(false);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && allowedTypes.some(type => droppedFile.type === type || droppedFile.name.endsWith(type.replace('.', '')))) {
      setFile(droppedFile);
      setTitle(formatTitleFromFilename(droppedFile.name));
      setGenre('Other');
      setAuthor('Unknown');
      // Extract thumbnail (reuse logic from handleFileSelect)
      const ext = droppedFile.name.split('.').pop()?.toLowerCase();
      if (ext === 'pdf') {
        try {
          const fileReader = new FileReader();
          fileReader.onload = async function() {
            const typedarray = new Uint8Array(this.result as ArrayBuffer);
            const pdf = await pdfjsLib.getDocument({ data: typedarray }).promise;
            const page = await pdf.getPage(1);
            const viewport = page.getViewport({ scale: 1.5 });
            const canvas = document.createElement('canvas');
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            const context = canvas.getContext('2d');
            if (context) {
              await page.render({ canvasContext: context, viewport }).promise;
              setThumbnail(canvas.toDataURL('image/png'));
            }
          };
          fileReader.readAsArrayBuffer(droppedFile);
        } catch (err) {
          setThumbnail(null);
        }
      } else if (ext === 'epub') {
        try {
          const fileReader = new FileReader();
          fileReader.onload = async function() {
            const epub = ePub(this.result);
            const coverUrl = await epub.coverUrl();
            if (coverUrl) {
              setThumbnail(coverUrl);
            } else {
              setThumbnail(null);
            }
          };
          fileReader.readAsArrayBuffer(droppedFile);
        } catch (err) {
          setThumbnail(null);
        }
      } else {
        setThumbnail(null);
      }
    } else {
      alert('Only PDF, EPUB, MOBI, and TXT files are allowed.');
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && allowedTypes.some(type => selectedFile.type === type || selectedFile.name.endsWith(type.replace('.', '')))) {
      setFile(selectedFile);
      setTitle(formatTitleFromFilename(selectedFile.name));
      setGenre('Other');
      setAuthor('Unknown');
      // Extract thumbnail
      const ext = selectedFile.name.split('.').pop()?.toLowerCase();
      if (ext === 'pdf') {
        try {
          const fileReader = new FileReader();
          fileReader.onload = async function() {
            const typedarray = new Uint8Array(this.result as ArrayBuffer);
            const pdf = await pdfjsLib.getDocument({ data: typedarray }).promise;
            const page = await pdf.getPage(1);
            const viewport = page.getViewport({ scale: 1.5 });
            const canvas = document.createElement('canvas');
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            const context = canvas.getContext('2d');
            if (context) {
              await page.render({ canvasContext: context, viewport }).promise;
              const dataUrl = canvas.toDataURL('image/png');
              setThumbnail(dataUrl);
              setCover(dataURLtoFile(dataUrl, 'thumbnail.png'));
            }
          };
          fileReader.readAsArrayBuffer(selectedFile);
        } catch (err) {
          setThumbnail(null);
          setCover(null);
        }
      } else if (ext === 'epub') {
        try {
          const fileReader = new FileReader();
          fileReader.onload = async function() {
            const epub = ePub(this.result);
            const coverUrl = await epub.coverUrl();
            if (coverUrl) {
              setThumbnail(coverUrl);
              // Fetch the coverUrl as blob and convert to File
              fetch(coverUrl).then(res => res.blob()).then(blob => {
                setCover(new File([blob], 'thumbnail.png', { type: blob.type }));
              });
            } else {
              setThumbnail(null);
              setCover(null);
            }
          };
          fileReader.readAsArrayBuffer(selectedFile);
        } catch (err) {
          setThumbnail(null);
          setCover(null);
        }
      } else {
        setThumbnail(null);
        setCover(null);
      }
    } else {
      alert('Only PDF, EPUB, MOBI, and TXT files are allowed.');
    }
  };

  const handleCoverSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedCover = e.target.files?.[0];
    if (selectedCover) {
      setCover(selectedCover);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !title || !author || !genre) return;
    setIsSubmitting(true);
    setUploadProgress(0);
    try {
      await onUpload({ file, title, author, genre, cover, setUploadProgress });
      setFile(null);
      setCover(null);
      setTitle('');
      setAuthor('');
      setGenre('Adventure');
      setUploadProgress(null);
      onClose();
    } catch (err) {
      alert('Failed to upload book. Please try again.');
      setUploadProgress(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm px-2 py-6">
      <form
        className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full p-4 md:p-8 border border-gray-100 flex flex-col gap-5 animate-fade-in max-h-[90vh] overflow-y-auto"
        onSubmit={handleSubmit}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-5 right-5 text-gray-400 hover:text-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-400 rounded-full p-1.5 bg-white shadow"
          aria-label="Close"
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">Add a New Book</h2>
        <p className="text-sm text-gray-500 mb-4 text-center">Upload your book file and fill in the details below.</p>

        {/* Drag & Drop Upload area */}
        <div
          className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-6 transition-colors duration-200 ${dragActive ? 'border-orange-500 bg-orange-50' : 'border-gray-300 bg-gray-50'}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{ cursor: 'pointer' }}
        >
          <input
            type="file"
            accept={allowedTypes.join(',')}
            ref={fileInputRef}
            onChange={handleFileSelect}
            required
            className="hidden"
          />
          <svg className="w-12 h-12 text-orange-400 mb-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M7 16V4a1 1 0 011-1h8a1 1 0 011 1v12M7 16l-2 2m0 0l2 2m-2-2h12" />
          </svg>
          <span className="text-gray-700 font-medium">Drag & drop your book file here</span>
          <span className="text-gray-500 text-sm mt-1">or <span className="text-orange-600 underline">Browse</span></span>
          {file && <span className="mt-2 text-green-600 text-sm">Selected: {file.name}</span>}
        </div>
        {/* Progress Bar */}
        {uploadProgress !== null && (
          <div className="w-full mb-4">
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-2 bg-orange-500 transition-all duration-200"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <div className="text-xs text-gray-500 mt-1 text-center">Uploading: {uploadProgress}%</div>
          </div>
        )}

        {/* Book Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
          <div>
            <label className="block text-sm font-medium text-gray-700">Title <span className="text-orange-600">*</span></label>
            <input type="text" className="w-full border rounded px-3 py-2" value={title} onChange={e => setTitle(e.target.value)} required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Author <span className="text-orange-600">*</span></label>
            <input type="text" className="w-full border rounded px-3 py-2" value={author} onChange={e => setAuthor(e.target.value)} required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Genre <span className="text-orange-600">*</span></label>
            <select className="w-full border rounded px-3 py-2" value={genre} onChange={e => setGenre(e.target.value)} required>
              {genres.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
        </div>
        {thumbnail && (
          <div className="mb-4 flex flex-col items-center">
            <span className="block text-xs text-gray-500 mb-1">Extracted Thumbnail Preview</span>
            <img src={thumbnail} alt="Book thumbnail preview" className="w-24 h-32 object-cover rounded shadow" />
          </div>
        )}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Cover Image (optional)</label>
          <input type="file" accept="image/*" ref={coverInputRef} onChange={handleCoverSelect} />
        </div>

        <button
          type="submit"
          className="w-full bg-orange-600 text-white py-2 rounded font-semibold hover:bg-orange-700 transition"
          disabled={!file || !title || !author || !genre || isSubmitting}
        >
          {isSubmitting ? 'Adding...' : 'Add Book'}
        </button>
      </form>
    </div>
  );
}; 